import { useNavigate } from "react-router-dom";
import { toast } from "components/ui/use-toast";
import { CAMPAIGN_MESSAGES, SLUG_STATUS_COPY } from "../../constants/messages";
import {
  CampaignType,
  IUpdateGamification,
  IStore,
} from "@/@types/gamification";
import {
  buildMechanicConfig,
  concurrencyPeriodParams,
} from "../../utils/mechanic-config";
import { getApiErrorMessage } from "../../utils/api-error";
import { bannerSourceKeyFor } from "../../utils/banner-source";
import { type UpdateGamificationFormData } from "../../validations/gamification.validation";
import { LEAVE_WITHOUT_GUARD } from "../../wizard/useUnsavedChangesGuard";

const PUBLISH_STATUS = "ACTIVE";

interface UseFormSubmitProps {
  campaignId: number;
  updateCampaign: (args: { id: number; data: IUpdateGamification }) => {
    unwrap: () => Promise<unknown>;
  };
  validatePeriod: (args: {
    startDate: string;
    endDate: string;
    excludeId?: number;
    campaignType?: CampaignType;
    competes?: boolean;
    group?: string;
  }) => {
    unwrap: () => Promise<{ valid: boolean; message: string }>;
  };
  onSlugConflict?: (message: string) => void;
}

export interface UpdateSubmitOptions {

  publish?: boolean;
}

export const useFormSubmit = ({
  campaignId,
  updateCampaign,
  validatePeriod,
  onSlugConflict,
}: UseFormSubmitProps) => {
  const navigate = useNavigate();

  const onSubmit = async (
    data: UpdateGamificationFormData,
    options: UpdateSubmitOptions = {},
  ) => {
    try {
      if (data.startDate && data.endDate) {
        let periodValidation: { valid: boolean; message: string };
        try {
          periodValidation = await validatePeriod({
            startDate: data.startDate,
            endDate: data.endDate,
            excludeId: campaignId,
            campaignType: data.campaignType,
            ...concurrencyPeriodParams(data.mechanicConfig),
          }).unwrap();
        } catch (error) {
          toast({
            title: "Não foi possível validar o período",
            description: getApiErrorMessage(
              error,
              "Não foi possível validar o período da campanha. Tente novamente.",
            ),
            variant: "destructive",
          });
          return;
        }

        if (!periodValidation.valid) {
          toast({
            title: "Período indisponível",
            description:
              periodValidation.message ||
              "O período informado conflita com outra campanha ativa do mesmo tipo.",
            variant: "destructive",
          });
          return;
        }
      }

      const mechanic = data.campaignType ?? CampaignType.ROULETTE;

      const payload: IUpdateGamification = {
        campaignName: data.campaignName?.trim(),
        slug: data.slug?.trim() ?? "",
        startDate: data.startDate,
        endDate: data.endDate,
        campaignType: data.campaignType,
        stores: data.stores as IStore[] | undefined,
        mechanicConfig: buildMechanicConfig(data.mechanicConfig, mechanic),
        faqItems: data.faqItems,
        ...(options.publish ? { status: PUBLISH_STATUS } : {}),
        regulations: data.regulations,
        banner3x4: data.banner3x4,
        banner16x9: data.banner16x9,
        banner3x4SourceKey: bannerSourceKeyFor(data.banner3x4, data.banner3x4SourceKey),
        banner16x9SourceKey: bannerSourceKeyFor(data.banner16x9, data.banner16x9SourceKey),
        allStoresSelected: data.allStoresSelected,
      };

      await updateCampaign({ id: campaignId, data: payload }).unwrap();
      if (options.publish) {
        toast({
          variant: "default",
          description: "Campanha publicada com sucesso!",
          duration: 5000,
        });
      }
      if (!options.publish) {
        toast(CAMPAIGN_MESSAGES.UPDATE.SUCCESS);
      }
      navigate("/crm/gestao-gameficacao", { state: LEAVE_WITHOUT_GUARD });
    } catch (error: unknown) {
      const description: string = getApiErrorMessage(
        error,
        CAMPAIGN_MESSAGES.UPDATE.ERROR.description,
      );
      toast({ ...CAMPAIGN_MESSAGES.UPDATE.ERROR, description });
      if (description === SLUG_STATUS_COPY.TAKEN) onSlugConflict?.(description);
    }
  };

  return { onSubmit };
};
